/**
 * Procurement Demo — Nami chat-input interceptor.
 *
 * THROWAWAY DEMO CODE. `useProcurementDemoInterceptor().tryIntercept(message)`
 * is called by the global chat renderer's send handler, after the AR and AP
 * interceptors. When demo mode is on and the message matches a scripted
 * procurement prompt, it streams the narration (no backend call), then opens the
 * page the answer points at, and returns `true` to skip the real send. A forecast
 * change ("sell 30% more over the next 6 months") is tried first: it narrates what
 * it will change, then applies it to the forecast store so the open page updates.
 * Replenishment jobs say their plan, wait for "Go ahead", change the page and
 * report; page questions and overview questions answer in the chat.
 * On an agreement's page its jobs explain what they will do and wait for a
 * go-ahead, and a question that is not a chip is answered from the agreement
 * when the match is strong enough.
 * On a forecast's details page, "Explain this forecast" plays the written
 * explanation of that forecast, the same one the page's button plays.
 */

import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useAgentState } from '@/context/agent-state.context';
import { isDemoMode } from '@/demo/core/demo-mode';
import {
  localContractAnswer,
  planContractJob,
  planJobReply,
} from '../pages/contracts/contract-chat';
import type { ContractJob } from '../pages/contracts/contract-chat';
import { bomStore } from '../pages/forecast/bom-store';
import {
  explainNarrationFor,
  isExplainForecastRequest,
} from '../pages/forecast/forecast-explain';
import {
  applyUplift,
  getForecastState,
} from '../pages/forecast/forecast-store';
import {
  parseOverrideRequest,
  planFollowUp,
  planOverride,
  seriesFromSearch,
} from './forecast-override';
import type { OverridePlan, OverrideRequest } from './forecast-override';
import { isProcurementDemoPath } from './demo-paths';
import { matchOverviewAnswer } from './overview-answers';
import { matchPageAnswer } from './page-answers';
import {
  planReplenishment,
  type ReplenishmentJob,
  type ReplenishmentTurn,
} from './replenishment-chat';
import {
  matchProcurementDemoScript,
  type ProcurementNavTarget,
} from './script';

type Navigate = ReturnType<typeof useNavigate>;

function openTarget(
  navigate: Navigate,
  target: ProcurementNavTarget,
): Promise<void> {
  switch (target.to) {
    case '/procurement/invoice-compliance':
      return navigate({ to: target.to, search: target.search });
    case '/procurement/contracts/$contractId':
      return navigate({
        to: target.to,
        params: target.params,
        search: target.search,
      });
    case '/procurement/materials/$materialId':
      return navigate({ to: target.to, params: target.params });
    case '/procurement/forecast/bom':
      return navigate({ to: target.to });
    case '/procurement/forecast/details':
      return navigate({ to: target.to, search: target.search });
  }
}

const FORECAST_DETAILS_PATH = '/procurement/forecast/details';

/** Time to let the opened page settle, so the change is seen landing. */
const SETTLE_MS = 450;

function settle(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, SETTLE_MS);
  });
}

export function useProcurementDemoInterceptor(): {
  tryIntercept: (message: string) => boolean;
  /** True on a procurement demo page, where these answers go before AR and AP. */
  ownsPage: boolean;
} {
  const { runDemoScriptedTurn } = useAgentState();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const series = seriesFromSearch(location.search);

  // A change that asked "which item?" and waits for the answer.
  const asked = useRef<{ message: string; request: OverrideRequest } | null>(
    null,
  );
  // A replenishment job Nami proposed and waits for the buyer to confirm.
  const proposed = useRef<ReplenishmentJob | null>(null);

  /**
   * A replenishment turn: an answer; a plan that waits for "Go ahead" (the
   * preview, if any, shows once the plan is read); or the confirmed job, which
   * changes the page while Nami works, then reports what changed.
   */
  const runReplenishment = useCallback(
    (message: string, turn: ReplenishmentTurn) => {
      switch (turn.kind) {
        case 'reply':
          void runDemoScriptedTurn(message, turn.narration, turn.apply);
          return;
        case 'propose':
          proposed.current = turn.job;
          void runDemoScriptedTurn(message, turn.job.plan, turn.job.preview);
          return;
        case 'run':
          void runDemoScriptedTurn(
            message,
            turn.job.report,
            undefined,
            turn.job.act,
          );
      }
    },
    [runDemoScriptedTurn],
  );

  /** Narrates a plan, then applies it once the narration is done. */
  const runPlan = useCallback(
    (message: string, plan: OverridePlan) => {
      if (plan.kind === 'reply') {
        void runDemoScriptedTurn(message, plan.narration);
        return;
      }
      const { destination } = plan;
      const apply = async () => {
        try {
          if (destination) {
            await openTarget(
              navigate,
              destination.to === 'bom'
                ? { to: '/procurement/forecast/bom' }
                : {
                    to: '/procurement/forecast/details',
                    search: { series: destination.series },
                  },
            );
            await settle();
          }
          applyUplift(plan.request);
        } catch (err) {
          console.error('[procurement-demo] forecast change threw', err);
        }
      };
      void runDemoScriptedTurn(message, plan.narration, apply);
    },
    [navigate, runDemoScriptedTurn],
  );

  /** Answers a forecast change, or the item that completes one Nami asked about. */
  const tryForecastOverride = useCallback(
    (message: string): boolean => {
      const context = { pathname, series };
      const swapApproved = bomStore.getState().swap === 'done';
      const request = parseOverrideRequest(message);
      const pending = asked.current;
      asked.current = null;
      if (!request) {
        if (!pending) return false;
        const followUp = planFollowUp(
          pending,
          message,
          context,
          getForecastState(),
          swapApproved,
        );
        if (!followUp) return false;
        if (followUp.kind === 'reply' && followUp.awaitingItem) {
          asked.current = pending;
        }
        runPlan(message, followUp);
        return true;
      }
      const plan = planOverride(
        message,
        request,
        context,
        getForecastState(),
        swapApproved,
      );
      if (plan.kind === 'reply' && plan.awaitingItem) {
        asked.current = { message, request };
      }
      runPlan(message, plan);
      return true;
    },
    [pathname, runPlan, series],
  );

  // A job on an agreement's page that explained itself and waits for a go-ahead.
  const pendingJob = useRef<ContractJob | null>(null);

  /** Opens the tab where a job's change shows, then makes the change. */
  const applyJob = useCallback(
    async (job: ContractJob) => {
      try {
        if (job.opens) {
          await navigate({
            to: '/procurement/contracts/$contractId',
            params: { contractId: job.contractId },
            search: { tab: job.opens },
          });
          await settle();
        }
        job.apply();
      } catch (err) {
        console.error('[procurement-demo] contract job threw', err);
      }
    },
    [navigate],
  );

  /** A job on an agreement's page: Nami explains it and asks, then acts on the go-ahead. */
  const tryContractJob = useCallback(
    (message: string): boolean => {
      const pending = pendingJob.current;
      pendingJob.current = null;
      const turn =
        (pending ? planJobReply(pending, message, pathname) : undefined) ??
        planContractJob(message, pathname);
      if (!turn) return false;
      if (turn.kind === 'run') {
        // Acts first, so the report describes what has changed on the page.
        void runDemoScriptedTurn(message, turn.narration, undefined, () =>
          applyJob(turn.job),
        );
        return true;
      }
      if (turn.kind === 'ask') pendingJob.current = turn.job;
      void runDemoScriptedTurn(message, turn.narration);
      return true;
    },
    [applyJob, pathname, runDemoScriptedTurn],
  );

  const tryIntercept = useCallback(
    (message: string): boolean => {
      // The procurement pages are demo pages, so they answer with or without
      // demo mode; elsewhere these answers need demo mode on.
      if (!isDemoMode() && !isProcurementDemoPath(pathname)) return false;
      // A proposed replenishment job only survives to the very next message.
      const proposedJob = proposed.current;
      proposed.current = null;
      if (tryForecastOverride(message)) return true;
      if (tryContractJob(message)) return true;
      // "Explain this forecast" on a forecast's details page plays the same
      // explanation as the page's own button.
      if (
        pathname === FORECAST_DETAILS_PATH &&
        isExplainForecastRequest(message)
      ) {
        void runDemoScriptedTurn(message, explainNarrationFor(series));
        return true;
      }
      // The replenishment pages' jobs: Nami says what it will do, waits for
      // "Go ahead", changes the page, then reports what changed.
      const replenishment = planReplenishment(message, pathname, proposedJob);
      if (replenishment) {
        runReplenishment(message, replenishment);
        return true;
      }
      // A page's own questions (contracts list, bid page, supplier, material)
      // and the overview's suggested questions answer in place, with sources.
      const answer =
        matchPageAnswer(message, pathname) ?? matchOverviewAnswer(message);
      if (answer) {
        void runDemoScriptedTurn(message, answer.narration);
        return true;
      }
      const script = matchProcurementDemoScript(message);
      if (!script) {
        // On an agreement's page, a question that is not a chip is answered from
        // the agreement when the match is strong; else the regular assistant
        // gets it.
        const local = localContractAnswer(message, pathname);
        if (local === undefined) return false;
        void runDemoScriptedTurn(message, local);
        return true;
      }

      const open = async () => {
        try {
          await openTarget(navigate, script.target);
        } catch (err) {
          console.error(
            `[procurement-demo] navigation for "${script.id}" threw`,
            err,
          );
        }
      };

      void runDemoScriptedTurn(message, script.narration, open);
      return true;
    },
    [
      navigate,
      pathname,
      runDemoScriptedTurn,
      runReplenishment,
      series,
      tryContractJob,
      tryForecastOverride,
    ],
  );

  return {
    tryIntercept,
    ownsPage: isProcurementDemoPath(pathname),
  };
}
